import { IconRefresh } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { InvoiceInformationForm } from '@/app/(protected)/(routes)/(external)/platform/_components/upgrade/invoice-information-form';
import { InvoiceInformationComponent } from '@/app/(protected)/(routes)/(secondary)/billing/invoice-information';
import { InvoiceInformationType, buildInvoiceInformationFormData } from '@/lib/billing-details-helper';
import { SelectOptionVM } from '@/lib/models/view-models/view-models';
import { cn } from '@/lib/utils';
import { Button } from '@/ui-lib/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui-lib/tooltip';

import { fetchPaymentMethods } from '../../../_components/api';
import { PaymentMethodSelector } from '../../../_components/PaymentMethodSelector';
import { useCheckoutAnalytics } from './checkoutAnalytics';

type Props = {
    areSubscriptionItemsChanged: boolean;
    countries: SelectOptionVM[];
    hasPaymentMethodError: boolean;
    invoiceInformation: InvoiceInformationType | null;
    isCancellationPending: boolean;
    isNewSubscription: boolean;
    isProcessing: boolean;
    onInvoiceInformationUpdated: () => void;
    onSelectPaymentMethod: (methodId: string | null) => void;
    onSubmit: (e?: React.BaseSyntheticEvent) => void;
    selectedPaymentMethodId: string | null;
    selectedPricesCount: number;
    userEmail: string;
    userName: string;
};

export const PaymentForm = ({
    areSubscriptionItemsChanged,
    countries,
    hasPaymentMethodError,
    invoiceInformation,
    isCancellationPending,
    isNewSubscription,
    isProcessing,
    onInvoiceInformationUpdated,
    onSelectPaymentMethod,
    onSubmit,
    selectedPaymentMethodId,
    selectedPricesCount,
    userEmail,
    userName,
}: Props) => {
    const router = useRouter();
    const [isEditingInvoiceInfo, setIsEditingInvoiceInfo] = useState(false);

    const { data: paymentMethodsResponse, isLoading: isLoadingPaymentMethods } = useQuery({
        queryKey: ['payment-methods'],
        queryFn: fetchPaymentMethods,
        initialData: { paymentMethods: [], count: 0 },
    });

    const paymentMethods = paymentMethodsResponse?.paymentMethods ?? [];

    // Auto-select default payment method (or first available) when payment methods are loaded
    useEffect(() => {
        if (paymentMethods.length > 0 && !selectedPaymentMethodId) {
            const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault);
            onSelectPaymentMethod(defaultPaymentMethod?.id || paymentMethods[0].id);
        }
    }, [paymentMethods]);

    const hasNoSelectedPrices = selectedPricesCount === 0;
    const analytics = useCheckoutAnalytics({
        isManagingSubscription: !isNewSubscription,
        isAuthenticated: true,
        showLoginForm: false,
        isEditingInvoiceInformation: isEditingInvoiceInfo,
        selectedPricesCount,
        selectedPaymentMethodId,
        paymentMethodsCount: paymentMethods.length,
        isLoadingPaymentMethods,
        hasPaymentMethodError,
        isProcessing,
        isLoggingIn: false,
        isFormDirty: areSubscriptionItemsChanged,
        isPendingCancellation: isCancellationPending,
    });

    const { headingTitle, headingParagraph } = isNewSubscription
        ? {
              headingTitle: `Hi, ${userName}`,
              headingParagraph: 'Please select a payment method to finalize your purchase.',
          }
        : {
              headingTitle: 'Manage credit card subscription',
              headingParagraph: hasNoSelectedPrices
                  ? 'You are canceling your credit card subscription. Your billing will be prorated.'
                  : 'You are updating your credit card subscription with multiple items. Your billing will be prorated based on the changes.',
          };

    const invoiceTeamId = invoiceInformation?.teamId;
    const invoiceFormInitialValues = invoiceInformation
        ? buildInvoiceInformationFormData(invoiceInformation)
        : undefined;

    return (
        <>
            <div className="space-y-3 pt-4 border-neutral-700">
                <h2 className="text-xl font-semibold text-neutral-100">{headingTitle}</h2>
                <p className="text-sm text-neutral-300 pt-2">{headingParagraph}</p>
            </div>
            {invoiceInformation &&
                (isEditingInvoiceInfo ? (
                    <div className="space-y-3 pt-4 border-t border-neutral-700">
                        <div className="flex justify-between items-center">
                            <h3 className="text-2xl font-semibold text-neutral-100">Edit Invoice Details</h3>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setIsEditingInvoiceInfo(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                        {invoiceTeamId != null && (
                            <InvoiceInformationForm
                                isCheckoutForm
                                teamId={invoiceTeamId}
                                countries={countries}
                                initialValues={invoiceFormInitialValues}
                                invoiceInformationAdded={() => {
                                    onInvoiceInformationUpdated();
                                    setIsEditingInvoiceInfo(false);
                                }}
                            />
                        )}
                    </div>
                ) : (
                    <div className="pt-4 border-t border-neutral-700">
                        <InvoiceInformationComponent
                            user={{ email: userEmail }}
                            invoiceInformation={invoiceInformation}
                            onEditClick={() => setIsEditingInvoiceInfo(true)}
                            hideUnsetFields
                        />
                    </div>
                ))}
            <form onSubmit={onSubmit} className={isEditingInvoiceInfo ? 'hidden' : undefined}>
                <div className="space-y-3 pt-4 border-t border-neutral-700">
                    <PaymentMethodSelector
                        paymentMethods={paymentMethods}
                        selectedMethodId={selectedPaymentMethodId}
                        onSelectMethod={onSelectPaymentMethod}
                        isLoading={isLoadingPaymentMethods}
                        hasPaymentMethodError={hasPaymentMethodError}
                    />

                    {paymentMethods.length === 0 && !isLoadingPaymentMethods && (
                        <p className="text-sm text-neutral-400 text-center py-2">No payment methods saved</p>
                    )}
                </div>

                <SubmitButton
                    areSubscriptionItemsChanged={areSubscriptionItemsChanged}
                    selectedPricesCount={selectedPricesCount}
                    hasSelectedPaymentMethod={selectedPaymentMethodId != null}
                    isCancellationPending={isCancellationPending}
                    isNewSubscription={isNewSubscription}
                    isProcessing={isProcessing}
                    onClick={() => analytics.trackCheckoutCtaClicked()}
                />
            </form>
            {!isNewSubscription && (
                <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    onClick={() => router.push('/billing')}
                >
                    Back to Billing
                </Button>
            )}
        </>
    );
};

type SubmitButtonProps = {
    areSubscriptionItemsChanged: boolean;
    selectedPricesCount: number;
    hasSelectedPaymentMethod: boolean;
    isCancellationPending: boolean;
    isNewSubscription: boolean;
    isProcessing: boolean;
    onClick: () => void;
};
const SubmitButton = ({
    areSubscriptionItemsChanged,
    selectedPricesCount,
    hasSelectedPaymentMethod,
    isCancellationPending,
    isNewSubscription,
    isProcessing,
    onClick,
}: SubmitButtonProps) => {
    const hasNoSelectedPrices = selectedPricesCount === 0;
    const getTexts = () => {
        if (isCancellationPending) {
            return {
                text: isProcessing ? 'Reactivating...' : 'Reactivate subscription',
                prefix: <IconRefresh className={cn('w-4 h-4 mr-2', { 'animate-spin': isProcessing })} />,
            };
        }

        if (isProcessing) {
            return { text: 'Processing...' };
        }

        if (isNewSubscription) {
            return {
                text: 'Complete order',
                tooltip: hasNoSelectedPrices ? 'Please select at least one report to continue' : undefined,
            };
        }

        return {
            text: hasNoSelectedPrices ? 'Cancel subscription' : 'Update subscription',
            tooltip: areSubscriptionItemsChanged
                ? undefined
                : 'Please make changes to your credit card subscription to update it',
        };
    };

    const { text, tooltip, prefix } = getTexts();
    const disabledReason = (() => {
        if (isProcessing) return 'processing';
        if (!hasSelectedPaymentMethod) return 'no-payment-method';
        if (isNewSubscription && hasNoSelectedPrices) return 'no-selected-prices';
        if (!isNewSubscription && !isCancellationPending && !areSubscriptionItemsChanged) return 'no-changes';
        return null;
    })();
    const disabled = disabledReason !== null;

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div>
                        <Button
                            variant="cta"
                            size="lg"
                            className={cn('w-full mt-6', {
                                'opacity-50 cursor-not-allowed': disabled && !isProcessing,
                            })}
                            disabled={disabled}
                            type="submit"
                            isLoading={isProcessing}
                            data-testid="checkout-button"
                            data-disabled-reason={disabledReason ?? undefined}
                            data-selected-prices-count={String(selectedPricesCount)}
                            data-has-selected-payment-method={String(hasSelectedPaymentMethod)}
                            data-is-new-subscription={String(isNewSubscription)}
                            data-is-processing={String(isProcessing)}
                            data-is-cancellation-pending={String(isCancellationPending)}
                            data-are-subscription-items-changed={String(areSubscriptionItemsChanged)}
                            prefix={prefix}
                            onClick={onClick}
                        >
                            {text}
                        </Button>
                    </div>
                </TooltipTrigger>
                {tooltip && (
                    <TooltipContent data-testid="disabled-checkout-tooltip" className="text-sm">
                        {tooltip}
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
};
